import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Formik } from 'formik'
import * as config from 'config'
import ActionButton from 'uiComponents/buttons'
import { SingleSelectFieldFormik } from 'uiComponents/input'
import RadioFieldFormik from 'uiComponents/input/radio/radioFieldFormik'
import { ModalDialog, ModalDialogTitle } from 'uiComponents/popups/modal'
import {
    isDateRangeMoreThanOneDay,
    OrdersPageQueryResult,
    useDownloadLink,
    UseDownloadLinkPayload,
    useOrdersPageQuery,
} from 'orders/orders/utils'
import { ORDER_PATHS } from 'orders/paths'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import { useScheduleAsyncExportMutation } from 'orders/reduxQueries'
import './styles.scss'
import { useAppSelector } from 'store/hooks'
import { ScheduleAsyncExportPayload } from 'orders/ordersService'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import { format as formatDate } from 'date-fns'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'

enum ExportType {
    Orders = 'export/orders/',
    OrdersDetails = 'export/orders/details/',
    OrdersDiscounts = 'export/orders/discounts/',
    OrdersRefunds = 'export/orders/refunds/',
    UnusedGiftCards = 'sold_unused_giftcards/export/',
}

const backofficeEndpoint = config.getRequired('backoffice-endpoint')
const ExportOptions = [
    { value: ExportType.Orders, name: 'Orders' },
    { value: ExportType.OrdersDetails, name: 'Order Details' },
    { value: ExportType.OrdersDiscounts, name: 'Order Discounts' },
    { value: ExportType.OrdersRefunds, name: 'Order Refunds' },
    { value: ExportType.UnusedGiftCards, name: 'Unused gift cards' },
]

interface FormValues {
    type: ExportType
    format: 'csv' | 'xlsx'
    dateSelection: 'all' | 'current'
}

const InitialExportValues: FormValues = {
    type: ExportType.Orders,
    format: 'csv',
    dateSelection: 'all',
}

const ExportOrders = () => {
    const location = useLocation()
    const accountSlug = useAppSelector((state) => state.preferences.activeAccount ?? '')
    const orderPageQuery = useOrdersPageQuery()
    const downloadLink = useDownloadLink()
    const [scheduleAsyncExport] = useScheduleAsyncExportMutation()
    const history = useHistory()

    const closeModal = () => {
        history.push(location.pathname.replace(`/${ORDER_PATHS.internalPaths.orderPath.export}`, '') + location.search)
    }

    const getScheduleAsyncExportProps = (
        path: ExportType,
        format: string,
        accountSlug: string,
        orderPageQuery: OrdersPageQueryResult,
        ignoreDateFilter: boolean,
    ): ScheduleAsyncExportPayload | UseDownloadLinkPayload => {
        if (path === ExportType.UnusedGiftCards) {
            return {
                search: orderPageQuery.search,
                account_slug: accountSlug,
                export_format: format,
                timezone_offset: new Date().getTimezoneOffset(),
                locations: getFiltersFromUrl('location_id'),
                filter_by_date: orderPageQuery.date_range_type === 'sale' ? 'created_at' : 'event_date',
                date_from: ignoreDateFilter ? '2000-01-01' : orderPageQuery.date_from,
                date_to: ignoreDateFilter ? formatDate(new Date(), 'yyyy-MM-dd') : orderPageQuery.date_to,
            }
        }
        return getDownloadLinkProps(format, orderPageQuery, ignoreDateFilter)
    }

    const getDownloadLinkProps = (
        format: string,
        orderPageQuery: OrdersPageQueryResult,
        ignoreDateFilter: boolean,
    ): UseDownloadLinkPayload => {
        return {
            ...orderPageQuery,
            export_format: format,
            locations: getFiltersFromUrl('location_id'),
            date_from: ignoreDateFilter ? '2000-01-01' : orderPageQuery.date_from,
            date_to: ignoreDateFilter ? formatDate(new Date(), 'yyyy-MM-dd') : orderPageQuery.date_to,
        }
    }

    const getDownloadLink = (path: ExportType, dateRangeMoreThanOneDay: boolean) => {
        if (path === ExportType.UnusedGiftCards) {
            return `${backofficeEndpoint}dashboard_api/sold_unused_giftcards/export/schedule`
        }
        return `${backofficeEndpoint}dashboard_api/accounts/${accountSlug}/${path}${
            dateRangeMoreThanOneDay ? 'schedule/' : ''
        }`
    }

    const onSubmit = async (values: FormValues) => {
        try {
            const ignoreDateFilter =
                (values.type === ExportType.UnusedGiftCards || orderPageQuery.search) && values.dateSelection === 'all'
            const dateRangeMoreThanOneDay = isDateRangeMoreThanOneDay(orderPageQuery) || ignoreDateFilter

            const endpoint = getDownloadLink(values.type, dateRangeMoreThanOneDay)

            if (dateRangeMoreThanOneDay || values.type === ExportType.UnusedGiftCards) {
                const payload = getScheduleAsyncExportProps(
                    values.type,
                    values.format,
                    accountSlug,
                    orderPageQuery,
                    ignoreDateFilter,
                )

                await scheduleAsyncExport({ endpoint, payload })
                showSuccessMessage('Request was placed successfully. An email will be sent to you shortly.')
                closeModal()
            } else {
                const payload = getDownloadLinkProps(values.format, orderPageQuery, ignoreDateFilter)

                await downloadLink(endpoint, payload)
                showSuccessMessage('Report generation may take up to 30 seconds and will download automatically.')
                closeModal()
                return
            }
        } catch {
            showErrorMessage('Oops! Something went wrong. Please try again.')
        }
    }

    return (
        <ModalDialog onDismiss={closeModal} interactive fromTop="20%" className="orders-export-dialog">
            <ModalDialogTitle>Export orders</ModalDialogTitle>
            <Formik initialValues={InitialExportValues} onSubmit={onSubmit} enableReinitialize>
                {({ values, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="questions-container">
                            <div className="type">
                                <h6>Export type</h6>
                                <SingleSelectFieldFormik
                                    name="type"
                                    options={ExportOptions}
                                    id="export-type-dropdown"
                                />
                            </div>
                            <div className="format">
                                <h6>Format</h6>
                                <div className="format-row">
                                    <RadioFieldFormik
                                        variant="inline-reverse"
                                        name="format"
                                        value="csv"
                                        label=".CSV"
                                        checkMarkSize="medium"
                                        className={values.format === 'csv' ? 'selected' : ''}
                                        id="export-csv"
                                    />
                                    <RadioFieldFormik
                                        variant="inline-reverse"
                                        name="format"
                                        value="xlsx"
                                        label=".XLSX"
                                        checkMarkSize="medium"
                                        className={values.format === 'xlsx' ? 'selected' : ''}
                                        id="export-xlsx"
                                    />
                                </div>
                            </div>
                            {(values.type === ExportType.UnusedGiftCards || orderPageQuery.search) && (
                                <div className="date">
                                    <h6>Time period</h6>
                                    <div className="format-row">
                                        <RadioFieldFormik
                                            variant="inline-reverse"
                                            name="dateSelection"
                                            value="all"
                                            label="All time"
                                            checkMarkSize="medium"
                                            className={values.dateSelection === 'all' ? 'selected' : ''}
                                            id="export-all-dates"
                                        />
                                        <RadioFieldFormik
                                            variant="inline-reverse"
                                            name="dateSelection"
                                            value="current"
                                            label="Currently selected"
                                            checkMarkSize="medium"
                                            className={values.dateSelection === 'current' ? 'selected' : ''}
                                            id="export-current-dates"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="buttons-container">
                            <CancelButton
                                size="large"
                                secondary
                                onClick={closeModal}
                                data-testid="export-cancel-button"
                            >
                                Cancel
                            </CancelButton>
                            <ActionButton size="large" kind="default" type="submit" data-testid="export-exports-button">
                                Export
                            </ActionButton>
                        </div>
                    </form>
                )}
            </Formik>
        </ModalDialog>
    )
}

export default ExportOrders
